// Added by Jonghyung Lee on 2021-01-13
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { FC, useState, useEffect, useMemo, useCallback } from 'react'
import { observer } from 'mobx-react'
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles'
import { useSnackbar, SnackbarKey } from 'notistack'

// components
import Drawer, { DrawerProps } from '@/components/_primes/v2/Drawer'
import { IconButton, OutlinedButton } from '@/components/_primes/v2'
import { ClearAllIcon, DoneAllIcon, NotificationsOnIcon, NotificationsOffIcon } from '@/components/_primes'
import Notification from '../NotificationsItem'
import { ConfirmDialog } from '@/components/common/v2/Dialog/ConfirmDialog'

// helper
import Intl from '@/helper/Intl'
import {
  NotificationItem,
  FROM_NOTI_CENTER,
  INTERVAL_FETCH_TIME,
  ReqGetNotifications,
  PAGE_SIZE,
} from '@/helper/define'
import { UserElementProps } from '@/stores/mgmt/user'

// store
import useStore from '@/stores/global/hooks/useStore'
import Logger from '@/helper/Logger'

interface NotificationsDrawerProps extends DrawerProps {
  setOpen: () => void
}

let enqueuedKeys: SnackbarKey[] = []
/** Notification을 위한 Drawer. 기본적으로 _primes/v2/Drawer를 사용한다 */
const NotificationsDrawer: FC<NotificationsDrawerProps> = observer(({ anchor = 'right', open, setOpen, ...props }) => {
  const classes = useStyles()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Store
  const {
    notification: {
      elements: notificationList,
      unreadElements: unreadNotificationsList,
      getElements: getNotifications,
      getPopupElements: getUnreadNotifications,
      updateElements: setReadAllNotifications,
      deleteElements: deleteAllNotifications,
      clearElements: clearUnreadNotificationsList,
      hasNextPage,
    },
    userAuth: { userId },
    user: { getElement: getUserInfo, updateElement: updateUserInfo },
  } = useStore()

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // states
  const [inited, setInited] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [isUnMute, setIsUnMute] = useState<boolean>(false)
  const [user, setUser] = useState<UserElementProps>()
  const [unreadNotifications, setUnreadNotifications] = useState<NotificationItem[]>([])
  const [queryParam, setQueryParam] = useState<ReqGetNotifications>({
    page_index: 1,
    page_size: PAGE_SIZE,
    order_by: 'created_at:desc',
    fk_user_id: userId,
    filter: JSON.stringify({ '[read]': false }),
  })
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [dialogMessage, setDialogMessage] = useState<string>('')

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // fetches
  const getUserSetting = useCallback(async () => await getUserInfo(userId), [getUserInfo])
  const getNotificationsList = useCallback(async (params) => await getNotifications(params), [getNotifications])
  const getUnreadNotificationList = useCallback(async (params) => await getUnreadNotifications(params), [
    getUnreadNotifications,
  ])

  const initNotificationDrawer = async () => {
    try {
      const [userInfo, notifications] = await Promise.all([
        getUserSetting(),
        getNotificationsList({ ...queryParam, page_index: 0, page_size: 0 }),
      ])
      setUser(userInfo)

      const { noti_enabled: notiEnabled } = userInfo
      setIsUnMute(notiEnabled)

      /** 최초에는 Unread 상태인 것만을 뽑아서 몇 개인지 알림을 준다 */
      const unreadCnt = notifications.length
      if (0 < unreadCnt) {
          const msgText = Intl.getIntlTextWithOption({ id: 'htmljs_react.worklist.notificationsdrawer.text_unread_count' }, { unreadCnt })
          const message = `### Notification\n${msgText}`
          const key = enqueueSnackbar(message, { key: `${FROM_NOTI_CENTER}_unread${unreadCnt}`, onClick: setOpen })
          enqueuedKeys = [...enqueuedKeys, key]
        }
        setUnreadNotifications(notifications)
    } catch (e) {
      Logger.errLog(`[Notifications Drawer] Can not init notification ${e}`)
    } finally {
      setInited(true)
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // i18n
  const i18nText = useMemo(
    () => ({
      btnShowMore: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsdrawer.btn_show_more' }),
      msgDeleteAllNotification: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsdrawer.msg_delete_all_notifications' }),
      titleNotifications: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsdrawer.title_notifications' }),
      textNoNotifications: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsdrawer.text_no_notifications' }),
      tooltipClearAll: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsdrawer.tooltip_clear_all' }),
      tooltipMarkAsAllRead: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsdrawer.tooltip_mark_as_all_read' }),
      textLoading: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsdrawer.text_loading' }),
      textMute: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsdrawer.text_mute' }),
      textUnmute: Intl.getIntlText({ id: 'htmljs_react.worklist.notificationsdrawer.text_unmute' }),
    }),
    [inited]
  )

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // effects
  /** for init only */
  useEffect(() => {
    if (inited) return
    /** System Engineer */
    if (userId === 0) return
    initNotificationDrawer()
    return () => clearUnreadNotificationsList()
  }, [])

  // Unread 형태의 리스트만 가져온다 / Drawer open 시에는 중단
  useEffect(() => {
    if (!inited) return
    let timeoutId: NodeJS.Timeout | undefined
    // Notification Drawer가 떠있는 경우에는 단축키 등의 키보드 이벤트를 모두 막는다
    const preventKeyboardEvent = (e: KeyboardEvent) => e.stopPropagation()
    if (open) {
      if (timeoutId) clearTimeout(timeoutId)
      document.addEventListener('keydown', preventKeyboardEvent, false)
      /** List를 얻어오는 시점에 이미 완료되었지만 아직 알림이 오지 않은 경우 이미 조회되었으므로 알림된 것으로 처리 */
      getNotificationsList({ ...queryParam, filter: JSON.stringify({}) }).then((result) => {
        setUnreadNotifications(result)
        setLoading(false)
      })
    } else {
      const unmutedParam = { ...queryParam, page_index: 0, page_size: 0 }
      /** Mute 상태의 User라면 source: system인 항목만을 Popup한다 (2021-12-27 System과 System 아닌 것의 구분이 없으므로 우선 모두 Mute 처리) */
      // const mutedParam = { ...unmutedParam, filter: JSON.stringify({ source: 'system', '[read]': false }) }
      // const params = isUnMute ? unmutedParam : mutedParam

      /**
       * Close될 때 밀린 notifications를 한번 조회하고 timeout recursive로 설정한다
       * setInterval로 처리하면 이전 요청이 끝나든 그렇지 않든 무조건 재요청을 하기 때문
       */
      /** Mute 상태가 아닐 경우에만 Fetch */
      if (isUnMute) getUnreadNotificationList(unmutedParam)
      setLoading(true)
      timeoutId =
        userId === 0
          ? undefined
          : setTimeout(async function getNoti() {
              if (isUnMute) await getUnreadNotificationList(unmutedParam)
              timeoutId = setTimeout(getNoti, INTERVAL_FETCH_TIME)
            }, INTERVAL_FETCH_TIME)
    }

    return () => {
      setLoading(true)
      document.removeEventListener('keydown', preventKeyboardEvent, false)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [open, inited, queryParam, isUnMute, getNotificationsList, getUnreadNotificationList])

  useEffect(() => {
    return () => {
      /** close 될 때 page size를 초기화 */
      setQueryParam((prev) => ({ ...prev, page_size: PAGE_SIZE }))
      /** 로그아웃하면 Snackbar가 노출되지 않도록 변경 */
      enqueuedKeys.forEach((item) => closeSnackbar(item))
    }
    // eslint-disable-next-line
  }, [open])

  useEffect(() => {
    // unread가 update되면 enqueueSnackbar하자
    if (unreadNotificationsList.length === 0) return

    if (unreadNotifications.length === 0) {
      const unread = unreadNotificationsList.map((unreadNotification: NotificationItem) => {
        const { id, message } = unreadNotification
        const key = enqueueSnackbar(message, { key: `${FROM_NOTI_CENTER}_${id}`, onClick: setOpen })
        enqueuedKeys = [...enqueuedKeys, key]
        return unreadNotification
      })
      return setUnreadNotifications(unread)
    }

    const prevLastItemId: number = Number(unreadNotifications[0].id)
    const currentLastItemID: number = Number(unreadNotificationsList[0].id)

    if (prevLastItemId < currentLastItemID) {
      const unread = unreadNotificationsList
        .filter(({ id }: NotificationItem) => Number(id) > prevLastItemId)
        .map((unNoticed: NotificationItem) => {
          const { id: filteredId, message } = unNoticed
          const key = enqueueSnackbar(message, { key: `${FROM_NOTI_CENTER}_${filteredId}`, onClick: setOpen })
          enqueuedKeys = [...enqueuedKeys, key]
          return unNoticed
        })
      return setUnreadNotifications(unread)
    }
    // eslint-disable-next-line
  }, [unreadNotificationsList])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // handler
  const handleClickAllRead = async () => {
    await setReadAllNotifications(userId)
    setQueryParam({ ...queryParam })
  }

  const handleClickAllClear = () => {
    setDialogMessage(i18nText.msgDeleteAllNotification)
    return setDialogOpen(true)
  }

  const handleDialogConfirmBtn = async () => {
    try {
      await deleteAllNotifications(userId)
      return setDialogOpen(false)
    } catch (error) {
      Logger.errLog(`[NotificationsDrawer] Delete Failed : ${error}`)
    }
  }

  const handleMute = async () => {
    const updatedUserInfo = { ...user, noti_enabled: !isUnMute }
    delete updatedUserInfo.expired_at
    try {
      await updateUserInfo(userId, updatedUserInfo)
      setIsUnMute(!isUnMute)
    } catch (error) {
      Logger.errLog(`[NotificationsDrawer] Update User Enabled Failed : ${error}`)
    }
  }

  const handleClickShowMore = () => setQueryParam({ ...queryParam, page_size: queryParam.page_size! + PAGE_SIZE })

  return (
    <>
      <Drawer {...props} anchor={anchor} open={open}>
        <div className={classes.root}>
          <div className={classes.titleWrapper}>
            {i18nText.titleNotifications}
            <div className={classes.btnWrapper}>
              <IconButton
                title={isUnMute ? i18nText.textMute : i18nText.textUnmute}
                onClick={handleMute}
                disabled={loading}
              >
                {isUnMute ? <NotificationsOnIcon /> : <NotificationsOffIcon />}
              </IconButton>
              <IconButton
                title={i18nText.tooltipMarkAsAllRead}
                onClick={handleClickAllRead}
                disabled={notificationList.length === 0 || loading}
              >
                <DoneAllIcon />
              </IconButton>
              <IconButton
                title={i18nText.tooltipClearAll}
                onClick={handleClickAllClear}
                disabled={notificationList.length === 0 || loading}
              >
                <ClearAllIcon />
              </IconButton>
            </div>
          </div>
          {loading ? (
            <div className={classes.noNotificationWrapper}>{i18nText.textLoading}</div>
          ) : (
            <div
              className={
                notificationList.length === 0 ? classes.noNotificationWrapper : classes.notificationItemWrapper
              }
            >
              {notificationList.length === 0
                ? i18nText.textNoNotifications
                : notificationList.map((notification: NotificationItem) => (
                    <Notification
                      key={`notification-item${notification.id}`}
                      notificationId={notification.id}
                      message={notification.message}
                      createdAt={notification.created_at}
                      isRead={notification.read}
                      handleInvokeQuery={setQueryParam}
                    />
                  ))}
              {hasNextPage && (
                <div className={classes.moreButton}>
                  <OutlinedButton onClick={handleClickShowMore}>{i18nText.btnShowMore}</OutlinedButton>
                </div>
              )}
            </div>
          )}
        </div>
      </Drawer>
      <ConfirmDialog
        title={i18nText.titleNotifications}
        open={dialogOpen}
        messages={dialogMessage}
        hasCancelBtn
        onConfirmBtnClick={handleDialogConfirmBtn}
        onCloseBtnClick={() => setDialogOpen(false)}
      />
    </>
  )
})

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    titleWrapper: {
      fontSize: '1.5rem',
      paddingBottom: theme.spacing(),
      borderBottom: '1px solid rgb(99, 99, 99)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    noNotificationWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      color: 'rgba(255, 255, 255, 0.7)',
      flex: 1,
    },
    notificationItemWrapper: {
      margin: '1rem auto 0',
      flex: 1,
      height: '90%',
      overflow: 'hidden auto',
    },
    btnWrapper: {
      height: '43px',
      display: 'flex',
    },
    moreButton: {
      display: 'flex',
      justifyContent: 'center',
    },
  })
)

export default NotificationsDrawer
